<template>
  <div class="max-w-5xl mx-auto space-y-8 animate-fade-in pb-10">

    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Configuración de Empresa</h1>
        <p class="text-slate-500 dark:text-slate-400 text-sm mt-1">Gestiona la identidad legal, fiscal, DTE y visual de tu organización.</p>
      </div>

      <button
        @click="saveSettings"
        :disabled="isSaving || isLoading || isUploadingLogo"
        class="flex items-center gap-2 px-6 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-xl shadow-lg shadow-brand-500/30 transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
      >
        <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
        <Save v-else class="w-4 h-4" />
        {{ isSaving ? 'Guardando...' : 'Guardar Cambios' }}
      </button>
    </div>

    <div v-if="errorMessage" class="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-2 text-sm border border-red-100">
      <AlertCircle class="w-5 h-5" /> {{ errorMessage }}
    </div>

    <div v-if="isLoading" class="flex justify-center py-20">
      <Loader2 class="w-10 h-10 text-brand-500 animate-spin" />
    </div>

    <div v-else>
      <div class="border-b border-slate-200 dark:border-slate-800 mb-8">
        <nav class="flex flex-wrap gap-6" aria-label="Tabs">
          <button @click="activeTab = 'general'" class="pb-4 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-2" :class="activeTab === 'general' ? 'border-brand-500 text-brand-600 dark:text-brand-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'">
            <Building2 class="w-4 h-4" /> Perfil General
          </button>
          <button @click="activeTab = 'fiscal'" class="pb-4 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-2" :class="activeTab === 'fiscal' ? 'border-brand-500 text-brand-600 dark:text-brand-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'">
            <Receipt class="w-4 h-4" /> Datos Fiscales
          </button>
          <button @click="activeTab = 'dte'" class="pb-4 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-2" :class="activeTab === 'dte' ? 'border-brand-500 text-brand-600 dark:text-brand-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'">
            <Key class="w-4 h-4" /> Facturación Electrónica (DTE)
          </button>
        </nav>
      </div>

      <div v-if="activeTab === 'general'" class="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in">

        <div class="lg:col-span-1">
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 text-center">
            <label class="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">Logotipo</label>

            <div class="relative w-32 h-32 mx-auto mb-6 group cursor-pointer overflow-hidden rounded-full">
              <img
                :src="form.logo_url || 'https://pub-e7d67786.r2.dev/logo-placeholder.png'"
                class="w-full h-full object-contain bg-white border-4 border-slate-50 dark:border-slate-800 shadow-sm transition-opacity duration-300"
                :class="{ 'opacity-50': isUploadingLogo }"
                alt="Logo Empresa"
              />
              <div v-if="isUploadingLogo" class="absolute inset-0 flex items-center justify-center bg-black/20 z-20">
                <Loader2 class="w-8 h-8 text-white animate-spin" />
              </div>
              <label v-else class="absolute inset-0 bg-slate-900/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer z-10">
                <Upload class="w-6 h-6 text-white" />
                <input
                  type="file"
                  class="hidden"
                  accept="image/png, image/jpeg, image/svg+xml"
                  @change="handleLogoUpload"
                />
              </label>
            </div>
            <p class="text-xs text-slate-400 mb-4">Recomendado: PNG o SVG, 500x500px.</p>
            <label class="text-sm text-brand-600 font-medium hover:underline cursor-pointer">
              Subir nueva imagen
              <input type="file" class="hidden" accept="image/*" @change="handleLogoUpload" />
            </label>
          </div>
        </div>

        <div class="lg:col-span-2 flex flex-col gap-6">
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 md:p-8">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Razón Social</label>
                <div class="relative">
                  <Building2 class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.emitter_name" type="text" class="form-input-premium pl-10" />
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Nombre Comercial</label>
                <input v-model="form.trade_name" type="text" class="form-input-premium" />
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Sitio Web</label>
                <div class="relative">
                  <Globe class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.website" type="url" class="form-input-premium pl-10" />
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Correo</label>
                <div class="relative">
                  <Mail class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.email" type="email" class="form-input-premium pl-10" />
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Teléfono</label>
                <div class="relative">
                  <Phone class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.phone_number" type="tel" class="form-input-premium pl-10" />
                </div>
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Dirección Física</label>
                <div class="relative">
                  <MapPin class="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <textarea v-model="form.address" rows="2" class="form-input-premium pl-10 resize-none"></textarea>
                </div>
              </div>

              <div class="md:col-span-2 border-t border-slate-100 dark:border-slate-800 pt-6 mt-2">
                <h3 class="text-sm font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <Shield class="w-4 h-4 text-brand-600" /> Representación Legal
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Nombre Representante</label>
                    <input v-model="form.legal_representative_name" type="text" class="form-input-premium" placeholder="Nombre completo del apoderado" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Documento ID (Cédula/DUI)</label>
                    <input v-model="form.legal_representative_id" type="text" class="form-input-premium" placeholder="Identificación legal" />
                  </div>
                </div>
                <p class="text-xs text-slate-400 mt-2">Estos datos aparecerán al pie de cotizaciones formales y contratos generados.</p>
              </div>
            </div>
          </div>

          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 md:p-8">
            <h3 class="text-sm font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Printer class="w-4 h-4 text-brand-600" /> Preferencias de Recibos y Formatos
            </h3>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Formato de Factura x Defecto</label>
                <select v-model="form.invoice_default_format" class="form-input-premium">
                  <option :value="null">Seleccionar...</option>
                  <option value="TICKET_58">Ticket 58mm</option>
                  <option value="TICKET_80">Ticket 80mm</option>
                  <option value="PDF_A4">Documento A4 (PDF)</option>
                  <option value="PDF_LETTER">Documento Carta (PDF)</option>
                </select>
              </div>

              <div class="space-y-4 pt-2">
                <label class="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" v-model="form.print_normal_details" :true-value="1" :false-value="0" class="w-5 h-5 text-brand-600 rounded focus:ring-brand-500 border-slate-300 dark:border-slate-700 dark:bg-slate-800" />
                  <div>
                    <span class="block text-sm font-medium text-slate-700 dark:text-slate-300">Desglosar Créditos NORMALES</span>
                    <span class="block text-xs text-slate-500">Muestra abono a capital e intereses en el ticket.</span>
                  </div>
                </label>

                <label class="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" v-model="form.print_flat_details" :true-value="1" :false-value="0" class="w-5 h-5 text-brand-600 rounded focus:ring-brand-500 border-slate-300 dark:border-slate-700 dark:bg-slate-800" />
                  <div>
                    <span class="block text-sm font-medium text-slate-700 dark:text-slate-300">Desglosar Créditos FLAT</span>
                    <span class="block text-xs text-slate-500">Muestra abono a capital e intereses en el ticket.</span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div v-if="activeTab === 'fiscal'" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 md:p-8 animate-fade-in">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">

          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">RUC / NIT</label>
            <input v-model="form.emitter_nit" type="text" class="form-input-premium bg-slate-50 dark:bg-slate-800/50" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">NRC / Registro</label>
            <input v-model="form.nrc" type="text" class="form-input-premium" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Régimen Fiscal</label>
            <input v-model="form.tax_regime" type="text" class="form-input-premium" placeholder="Ej: Gran Contribuyente" />
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">País</label>
            <input v-model="form.country" type="text" class="form-input-premium" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Ciudad / Estado</label>
            <input v-model="form.city" type="text" class="form-input-premium" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Código Sucursal</label>
            <input v-model="form.code" type="text" class="form-input-premium" />
          </div>

          <div class="border-t border-slate-100 dark:border-slate-800 col-span-1 md:col-span-3 my-2"></div>

          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Moneda Base</label>
            <select v-model="form.currency_code" class="form-input-premium">
              <option value="NIO">Córdoba (NIO)</option>
              <option value="USD">Dólar (USD)</option>
              <option value="SVC">Colón (SVC)</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Símbolo Moneda</label>
            <input v-model="form.currency_symbol" type="text" class="form-input-premium" placeholder="$, C$, etc." />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Impuesto Defecto (IVA %)</label>
            <div class="relative">
              <input v-model="form.default_tax_rate" type="number" step="0.01" class="form-input-premium pr-8" />
              <span class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">%</span>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Retención Defecto (%)</label>
            <input v-model="form.default_retention_rate" type="number" step="0.01" class="form-input-premium" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Retención Grandes Contrib. (%)</label>
            <input v-model="form.default_major_contributors_rate" type="number" step="0.01" class="form-input-premium" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Monto Aplicar Retención</label>
            <input v-model="form.retention_amount" type="number" step="0.01" class="form-input-premium" />
          </div>

          <div class="md:col-span-3">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Notas al Pie (Factura)</label>
            <textarea v-model="form.footer_notes" rows="2" class="form-input-premium resize-none"></textarea>
          </div>
        </div>
      </div>

      <div v-if="activeTab === 'dte'" class="space-y-6 animate-fade-in">
        <div class="flex gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl">
          <AlertCircle class="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 class="text-sm font-bold text-blue-800 dark:text-blue-300">Configuración DTE</h4>
            <p class="text-sm text-blue-600 dark:text-blue-400 mt-1">Solo modificar con conocimiento técnico de Hacienda/Ministerio.</p>
          </div>
        </div>

        <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 md:p-8">

          <div class="mb-6 pb-6 border-b border-slate-200 dark:border-slate-800">
            <label class="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" v-model="form.send_dte" :true-value="1" :false-value="0" class="w-5 h-5 text-brand-600 rounded focus:ring-brand-500 border-slate-300 dark:border-slate-700 dark:bg-slate-800" />
              <div>
                <span class="block text-sm font-medium text-slate-700 dark:text-slate-300">Habilitar Transmisión DTE (Facturación Electrónica Activa)</span>
              </div>
            </label>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Ambiente</label>
              <div class="flex items-center gap-4">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="radio" v-model="form.dte_env" value="SB" class="text-brand-600 focus:ring-brand-500" />
                  <span class="text-sm text-slate-700 dark:text-slate-300">Sandbox (Pruebas)</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="radio" v-model="form.dte_env" value="PR" class="text-brand-600 focus:ring-brand-500" />
                  <span class="text-sm text-slate-700 dark:text-slate-300">Producción</span>
                </label>
              </div>
            </div>

            <div>
               <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Ruta Certificado / Nombre Key</label>
               <input v-model="form.dte_cert_path" type="text" class="form-input-premium" />
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">API Client ID / Token ID</label>
              <input v-model="form.dte_client_id" type="password" class="form-input-premium font-mono text-sm" placeholder="***************" />
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">API Client Secret</label>
              <input v-model="form.dte_client_secret" type="password" class="form-input-premium font-mono text-sm" placeholder="***************" />
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Contraseña del Certificado</label>
              <input v-model="form.dte_cert_password" type="password" class="form-input-premium font-mono text-sm" placeholder="***************" />
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Private Key Content (PEM)</label>
              <input v-model="form.dte_private_key_content" type="password" class="form-input-premium font-mono text-sm" placeholder="Contenido de la llave privada..." />
            </div>

            <div class="border-t border-slate-100 dark:border-slate-800 col-span-1 md:col-span-2 my-2"></div>

            <div>
               <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Código Departamento (Hacienda)</label>
               <input v-model="form.department_code" type="text" class="form-input-premium" />
            </div>

            <div>
               <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Código Municipio (Hacienda)</label>
               <input v-model="form.municipality_code" type="text" class="form-input-premium" />
            </div>

            <div class="md:col-span-2">
               <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Actividad Económica (DTE)</label>
               <div class="flex gap-2">
                 <input v-model="form.dte_activity_code" type="text" class="form-input-premium w-1/4" placeholder="Código" />
                 <input v-model="form.dte_activity_desc" type="text" class="form-input-premium w-3/4" placeholder="Descripción de la actividad" />
               </div>
            </div>

          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import {
  Building2, Receipt, Key, Save, Upload,
  Globe, Mail, Phone, MapPin, AlertCircle, Loader2, Shield, Printer
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useUpload } from '@/composables/useUpload'; // Cambiado a useUpload

// Estado de la UI
const activeTab = ref('general');
const isLoading = ref(false);
const isSaving = ref(false);
const errorMessage = ref('');

// Composable useUpload
const { uploading: isUploadingLogo, uploadFile } = useUpload();

// Datos del Formulario con ABSOLUTAMENTE TODOS LOS CAMPOS
const form = reactive({
  id: null,
  code: '',
  emitter_name: '',
  trade_name: '',
  emitter_nit: '',
  tax_regime: '',
  currency_code: 'USD',
  default_tax_rate: 0,
  address: '',
  phone_number: '',
  email: '',
  logo_url: '',
  default_retention_rate: null,
  default_major_contributors_rate: null,
  retention_amount: null,
  city: null,
  country: '',
  website: '',
  footer_notes: '',
  invoice_default_format: null,
  print_flat_details: 0,
  print_normal_details: 1,
  nrc: '',
  department_code: null,
  municipality_code: null,
  dte_activity_code: null,
  dte_activity_desc: null,
  send_dte: 0,
  dte_client_id: '',
  dte_client_secret: null,
  dte_cert_password: null,
  dte_private_key_content: null,
  dte_cert_path: '',
  dte_env: 'SB',
  currency_symbol: '$',
  legal_representative_name: '',
  legal_representative_id: ''
});

// 1. CARGAR DATOS
onMounted(async () => {
  await loadCompanyData();
});

const loadCompanyData = async () => {
  isLoading.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    if (!user || !user.company) throw new Error("No se encontró la ID de la compañía.");

    const companyId = user.company;
    const response = await apiClient.get(`/company_config/${companyId}`);
    const data = response.data;

    // Asignación directa de todos los campos mapeados
    Object.assign(form, {
      id: data.id,
      code: data.code,
      emitter_name: data.emitter_name,
      trade_name: data.trade_name,
      emitter_nit: data.emitter_nit,
      tax_regime: data.tax_regime,
      currency_code: data.currency_code,
      default_tax_rate: data.default_tax_rate,
      address: data.address,
      phone_number: data.phone_number,
      email: data.email,
      logo_url: data.logo_url,
      default_retention_rate: data.default_retention_rate,
      default_major_contributors_rate: data.default_major_contributors_rate,
      retention_amount: data.retention_amount,
      city: data.city,
      country: data.country,
      website: data.website,
      footer_notes: data.footer_notes,
      invoice_default_format: data.invoice_default_format,
      print_flat_details: data.print_flat_details,
      print_normal_details: data.print_normal_details,
      nrc: data.nrc,
      department_code: data.department_code,
      municipality_code: data.municipality_code,
      dte_activity_code: data.dte_activity_code,
      dte_activity_desc: data.dte_activity_desc,
      send_dte: data.send_dte,
      dte_client_id: data.dte_client_id,
      dte_client_secret: data.dte_client_secret,
      dte_cert_password: data.dte_cert_password,
      dte_private_key_content: data.dte_private_key_content,
      dte_cert_path: data.dte_cert_path,
      dte_env: data.dte_env || 'SB',
      currency_symbol: data.currency_symbol,
      legal_representative_name: data.legal_representative_name,
      legal_representative_id: data.legal_representative_id
    });

  } catch (error) {
    console.error("Error cargando config:", error);
    errorMessage.value = "Error al cargar la configuración.";
  } finally {
    isLoading.value = false;
  }
};

// 2. SUBIR LOGO CON useUpload
const handleLogoUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Usamos el composable
  const url = await uploadFile(file, "company_logos");

  if (url) {
    form.logo_url = url; // Actualizamos la vista inmediatamente
  }
  // Limpiamos el input para permitir subir el mismo archivo si es necesario
  event.target.value = '';
};

// 3. GUARDAR CAMBIOS
const saveSettings = async () => {
  isSaving.value = true;
  errorMessage.value = '';

  try {
    if (!form.id) throw new Error("ID de compañía no válido");
    await apiClient.put(`/company_config/${form.id}`, form);
    alert("Configuración actualizada correctamente");
  } catch (error) {
    console.error("Error guardando:", error);
    errorMessage.value = "Error al guardar. Intente nuevamente.";
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.3s ease-out forwards;
}
</style>